import { Text as T } from '@eo-locale/preact';
import clsx from 'clsx';
import CircleTick from 'images/icons/CircleTick.svg';
import ImageIcon from 'images/icons/Image.svg';
import Location from 'images/icons/Location.svg';
import { useCallback, useEffect, useState } from 'react';
import { Review } from 'types/review';
import {
  ExtendedCarouselWidgetSettings,
  ExtendedFullPageWidgetSettings,
  ExtendedReviewsTabWidgetSettings,
  ExtendedReviewsWidgetSettings,
  ExtendedTopRatedProductsWidgetSettings,
} from 'types/settings';
import { formatDate } from 'utilities/content';
import { useLocale } from 'utilities/use-locale';

import { Button } from 'components/Button';
import { Icon } from 'components/Icon';
import { Image } from 'components/Image';
import { ImageSlider } from 'components/ImageSlider';
import { Link } from 'components/Link';
import { Modal } from 'components/Modal';
import { useSettings } from 'components/SettingsProvider';
import { Stack } from 'components/Stack';
import { Stars } from 'components/Stars';
import { Text } from 'components/Text';
import { TipVerified } from 'components/TipVerified';
import { TipWasImported } from 'components/TipWasImported';

import styles from './ReviewModal.module.scss';

type Settings = Pick<
  ExtendedCarouselWidgetSettings,
  'show_location' | 'show_thumbnail'
>;

interface ReviewModalProps {
  open: boolean;
  review: Review;
  settings: Settings;
  contentExpanded?: boolean;
  onNext?: () => void;
  onPrev?: () => void;
  onClose: () => void;
}

type AnyReviewsWidget =
  | ExtendedReviewsWidgetSettings
  | ExtendedCarouselWidgetSettings
  | ExtendedFullPageWidgetSettings
  | ExtendedReviewsTabWidgetSettings
  | ExtendedTopRatedProductsWidgetSettings;

export function ReviewModal({
  open,
  review,
  settings,
  contentExpanded,
  onNext,
  onPrev,
  onClose,
}: ReviewModalProps) {
  const {
    title,
    author,
    created_at,
    rating,
    location,
    images,
    body,
    product_handle,
    product_title,
    product_image_src,
    verified_purchase,
    pros,
    cons,
    source,
  } = review;

  const { show_location, show_thumbnail } = settings;
  const hasImages = images.length > 0;
  const [isShowBody, setShowBody] = useState(contentExpanded);
  const showBody = useCallback(() => setShowBody(true), []);
  const {
    utils: { shopDomain, isRtl },
    settings: GlobalSettings,
    show_date_creation,
  } = useSettings<AnyReviewsWidget>();

  const { locale } = useLocale();

  const customDateFormat =
    GlobalSettings.reviews.tweaks.custom_date_format_json;

  useEffect(() => {
    setShowBody(contentExpanded);
  }, [review.id, contentExpanded]);

  const reviewTitleMarkup = title && <Text variation="strong">{title}</Text>;

  const locationMarkup = show_location && location && (
    <Stack
      spacing="extraTight"
      alignment="center"
      distribution="trailing"
      wrap={false}
    >
      <Icon source={Location} color="inherit" />
      <Text size="small">{location}</Text>
    </Stack>
  );

  const buttonMarkup = !isShowBody && (
    <div>
      <Button monochrome plain onClick={showBody}>
        <Text uppercase>
          <T
            id="review_show_full_review_button"
            defaultMessage="show full review"
          />
        </Text>
      </Button>
    </div>
  );

  const thumbnailMarkup = show_thumbnail && (
    <div className={styles.ProductThumbnail}>
      <Image alt={product_title} source={product_image_src || ImageIcon} />
    </div>
  );

  const reivewProductNameMarkup = (
    <Stack spacing="tight" alignment="center" wrap={false}>
      {thumbnailMarkup}
      <Stack.Item fill>
        <Text truncate>
          <Link
            monochrome
            external
            url={`https://${shopDomain}/products/${product_handle}`}
          >
            {product_title}
          </Link>
        </Text>
      </Stack.Item>
    </Stack>
  );

  const prosMarkup = pros ? (
    <Stack spacing="extraTight" wrap={false} alignment="baseline">
      <Text size="small" variation="strong" as="span">
        <T id="review_pros_label" defaultMessage="Pros" />:
      </Text>
      <Stack.Item fill>
        <Text size="small">{pros}</Text>
      </Stack.Item>
    </Stack>
  ) : null;

  const consMarkup = cons ? (
    <Stack spacing="extraTight" wrap={false} alignment="baseline">
      <Text size="small" variation="strong" as="span">
        <T id="review_cons_label" defaultMessage="Cons" />:
      </Text>
      <Stack.Item fill>
        <Text size="small">{cons}</Text>
      </Stack.Item>
    </Stack>
  ) : null;

  const bodyMarkup = isShowBody ? (
    <div className={styles.ReviewBody}>
      <div className={styles.BodyContent}>
        <Stack vertical>
          <Stack.Item fill>
            <Text size="small">{body}</Text>
          </Stack.Item>
          {prosMarkup}
          {consMarkup}
        </Stack>
      </div>
      {reivewProductNameMarkup}
    </div>
  ) : (
    <div className={`${styles.ReviewBody} ${styles.ColumnReverse}`}>
      {reivewProductNameMarkup}
    </div>
  );

  const authorMarkup = (
    <Stack spacing="extraTight" alignment="center">
      <Text size="small" variation="strong">
        {author}
      </Text>
      {verified_purchase === 'VERIFIED' && (
        <Icon source={CircleTick} color="inherit" />
      )}
      {verified_purchase === 'VERIFIED' && <TipVerified source={source} />}
      <TipWasImported source={source} />
    </Stack>
  );

  const isShowDate =
    !GlobalSettings.reviews?.tweaks.hide_date_creation && show_date_creation;

  const publishDateMarkup = isShowDate && (
    <Text size="small" variation="subdued">
      {formatDate(created_at, locale, customDateFormat)}
    </Text>
  );

  const sliderMarkup = hasImages && (
    <div className={styles.SliderContainer}>
      <ImageSlider
        maxHeight="600px"
        images={images.map((image) => image.image_key)}
      >
        {onNext && onPrev && (
          <ImageSlider.Controls
            nextAction={{
              onAction: onNext,
            }}
            prevAction={{
              onAction: onPrev,
            }}
          />
        )}
      </ImageSlider>
    </div>
  );

  return (
    <Modal
      condensed
      large={hasImages}
      title={title}
      open={open}
      onClose={onClose}
    >
      <div className={clsx(styles.ReviewModal, isRtl && styles.Rtl)}>
        {sliderMarkup}
        <div className={styles.Review}>
          <div className={styles.ReviewContent}>
            <div className={styles.Header}>
              <Stack vertical spacing="extraTight">
                {authorMarkup}
                {publishDateMarkup}
              </Stack>
            </div>
            {reviewTitleMarkup}
            <Stack spacing="tight">
              <Stack.Item fill>
                <Stars value={rating} />
              </Stack.Item>
              {locationMarkup}
            </Stack>
            {buttonMarkup}
            {bodyMarkup}
          </div>
        </div>
      </div>
    </Modal>
  );
}
